
import { computed, defineComponent, PropType, reactive } from 'vue'

// components
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { carriers } from '@/core/realisations/carriers.realisation'
import { CarrierT } from '@/core/types/carriers.types'

export default defineComponent({
    components: {
        'button-dropdown': ButtonDropdown,
        'the-button': TheButton,
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
        isStandardContract: {
            type: [Boolean, Number],
            default: false,
        },
        codeOneC: {
            type: String,
            default: null,
        },
        carrier: {
            type: Object as PropType<CarrierT>,
            default: null,
        },
    },
    emits: ['update', 'send', 'deactivate', 'activate', 'tripartite-agreement'],
    setup(props, { emit }) {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_dogovor_loading: false,
            is_send_loading: false,
            is_deactivate_loading: false,
            is_activate_loading: false,
            is_1c_loading: false,
        })

        const emitUpdate = () => {
            emit('update')
        }

        const resendContract = async () => {
            if (state.is_dogovor_loading) return

            const answer = confirm('Вы уверены что хотите переотправить договор?')
            if (!answer) return

            try {
                state.is_dogovor_loading = true

                await companies.resendCompanyContract(props.id)
                notifications.notify({
                    title: 'Переотправлен',
                    description: 'Договор компании переотправлен',
                    type: 'success',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При переотправке договора произошла ошибка',
                })
            } finally {
                state.is_dogovor_loading = false
            }
        }

        const emitSend = () => {
            emit('send', props.carrier)
        }

        const emitActivate = () => {
            emit('activate', props.id)
        }

        const emitDeactivate = () => {
            emit('deactivate', props.id)
        }

        const emitTripartiteAgreement = () => {
            emit('tripartite-agreement')
        }

        const resendTo1C = async () => {
            if (state.is_1c_loading) return

            const answer = confirm('Вы уверены что хотите переотправить компанию 1С?')
            if (!answer) return

            try {
                state.is_1c_loading = true

                await carriers.resendTo1C(props.id)
                notifications.notify({ title: 'Компания переотправлена', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При переотправке компании произошла ошибка',
                })
            } finally {
                state.is_1c_loading = false
            }
        }

        const isCanResendContract = computed(() => {
            if (!props.isStandardContract) return false
            if (havePermissions(['company-resend-contract', 'all-root-permission'])) return true
            return false
        })

        const isCanResendTo1C = computed(() => {
            if (props.codeOneC) return false
            if (havePermissions(['company-resend-one-c', 'all-root-permission'])) return true

            return false
        })

        const isCanSendToContract = computed(() => {
            if (havePermissions(['company-send-contract', 'all-root-permission'])) return true

            return false
        })

        const isCanTripartiteAgreement = computed(() => {
            if (havePermissions(['cabinet.vehicle-owner-send-contract', 'all-root-permission'])) {
                if (props.carrier) {
                    return props.carrier.activity.some((activity) => activity.activityId === 2)
                }
            }

            return false
        })

        return {
            state,
            emitUpdate,
            resendContract,
            resendTo1C,
            emitSend,
            emitActivate,
            emitDeactivate,
            emitTripartiteAgreement,
            //
            isCanResendContract,
            isCanResendTo1C,
            isCanSendToContract,
            isCanTripartiteAgreement,
        }
    },
})
